import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock,
  VpnKey,
  Check,
  Close
} from '@mui/icons-material';
import Swal from 'sweetalert2';

function ChangePasswordController() {
  const [passwords, setPasswords] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    newPassword: false,
    confirmPassword: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    passwordsMatch: false,
    hasSpecialChar: false,
    hasMoreThan12OfLength: false
  });
  const navigate = useNavigate();

  useEffect(() => {
    // Verificar si existe el token de verificación
    const verificationToken = localStorage.getItem('verificationCodeToken') || localStorage.getItem('resetPasswordToken');
    if (!verificationToken) {
      Swal.fire({
        icon: 'error',
        title: 'Acceso denegado',
        text: 'No tienes autorización para acceder a esta página.',
        confirmButtonColor: '#4a148c',
        showClass: {
          popup: 'animate__animated animate__fadeInDown'
        },
        hideClass: {
          popup: 'animate__animated animate__fadeOutUp'
        }
      }).then(() => {
        navigate('/sign-in');
      });
    }
  }, [navigate]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setPasswords(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Actualizar el feedback en tiempo real
    if (field === 'newPassword') {
      updatePasswordStrength(value, passwords.confirmPassword);
    } else if (field === 'confirmPassword') {
      updatePasswordStrength(passwords.newPassword, value);
    }
    
    // Limpiar errores cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const updatePasswordStrength = (newPassword, confirmPassword) => {
    setPasswordStrength({
      minLength: newPassword.length >= 8,
      hasUppercase: /[A-Z]/.test(newPassword),
      hasLowercase: /[a-z]/.test(newPassword),
      hasNumber: /\d/.test(newPassword),
      passwordsMatch: newPassword === confirmPassword && confirmPassword.length > 0,
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
      hasMoreThan12OfLength: newPassword.length >= 12
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validatePasswords = () => {
    const newErrors = {};

    // Usar el estado de passwordStrength para validaciones más precisas
    if (!passwords.newPassword.trim()) {
      newErrors.newPassword = 'La nueva contraseña es requerida';
    } else if (!passwordStrength.minLength || !passwordStrength.hasUppercase || 
               !passwordStrength.hasLowercase || !passwordStrength.hasNumber) {
      newErrors.newPassword = 'La contraseña no cumple con todos los requisitos';
    }

    // Validar confirmación de contraseña
    if (!passwords.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Debe confirmar la nueva contraseña';
    } else if (!passwordStrength.passwordsMatch) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validatePasswords()) {
      return;
    }

    setLoading(true);

    try {
      const verificationToken = localStorage.getItem('resetPasswordToken');
      
      const response = await fetch('https://dprincess-training.servehttp.com/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${verificationToken}`
        },
        body: JSON.stringify({
          token: verificationToken,
          newPassword: passwords.newPassword
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Limpiar el token de verificación del localStorage
        localStorage.removeItem('verificationCodeToken');
        localStorage.removeItem('resetPasswordToken');
        localStorage.removeItem('verificationCode');

        // Mostrar mensaje de éxito
        Swal.fire({
          icon: 'success',
          title: '¡Contraseña actualizada!',
          text: 'Su contraseña ha sido cambiada exitosamente. Ahora puede iniciar sesión con su nueva contraseña.',
          confirmButtonText: 'Iniciar Sesión',
          confirmButtonColor: '#4a148c',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showClass: {
            popup: 'animate__animated animate__fadeInDown'
          },
          hideClass: {
            popup: 'animate__animated animate__fadeOutUp'
          },
          backdrop: `
            rgba(74, 20, 140, 0.1)
            left top
            no-repeat
          `
        }).then(() => {
          navigate('/sign-in');
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: data.message || 'No se pudo actualizar la contraseña. Por favor, inténtelo de nuevo.',
          confirmButtonColor: '#4a148c',
          showClass: {
            popup: 'animate__animated animate__shakeX'
          }
        });
      }
    } catch (error) {
      console.error('Error al cambiar la contraseña:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error de conexión',
        text: 'No se pudo conectar con el servidor. Por favor, verifique su conexión e inténtelo de nuevo.',
        confirmButtonColor: '#4a148c',
        showClass: {
          popup: 'animate__animated animate__shakeX'
        }
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      py: 4
    }}>
      <Paper
        elevation={8}
        sx={{
          padding: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
          boxShadow: '0 8px 32px rgba(74, 20, 140, 0.1)'
        }}
      >
        {/* Icono y título */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 3,
          flexDirection: 'column'
        }}>
          <Box sx={{
            bgcolor: '#4a148c',
            borderRadius: '50%',
            p: 2,
            mb: 2
          }}>
            <VpnKey sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography 
            component="h1" 
            variant="h4" 
            sx={{ 
              fontWeight: 'bold',
              color: '#4a148c',
              textAlign: 'center'
            }}
          >
            Nueva Contraseña
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#666',
              textAlign: 'center',
              mt: 1
            }}
          >
            Ingrese su nueva contraseña para completar el proceso
          </Typography>
        </Box>

        {/* Formulario */}
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
          <TextField
            margin="normal"
            required
            fullWidth
            name="newPassword"
            label="Nueva Contraseña"
            type={showPasswords.newPassword ? 'text' : 'password'}
            id="newPassword"
            value={passwords.newPassword}
            onChange={handleInputChange('newPassword')}
            error={!!errors.newPassword}
            helperText={errors.newPassword}
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: '#4a148c' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => togglePasswordVisibility('newPassword')}
                    edge="end"
                    disabled={loading}
                  >
                    {showPasswords.newPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#4a148c',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#4a148c',
              },
            }}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirmar Nueva Contraseña"
            type={showPasswords.confirmPassword ? 'text' : 'password'}
            id="confirmPassword"
            value={passwords.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: '#4a148c' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => togglePasswordVisibility('confirmPassword')}
                    edge="end"
                    disabled={loading}
                  >
                    {showPasswords.confirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#4a148c',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#4a148c',
              },
            }}
          />

          {/* Feedback en tiempo real de la contraseña */}
          {passwords.newPassword && (
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium', color: '#4a148c' }}>
                Requisitos de la contraseña:
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {/* Longitud mínima */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {passwordStrength.minLength ? (
                    <Check sx={{ fontSize: 16, color: '#4caf50' }} />
                  ) : (
                    <Close sx={{ fontSize: 16, color: '#f44336' }} />
                  )}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: passwordStrength.minLength ? '#4caf50' : '#f44336',
                      fontSize: '0.85rem'
                    }}
                  >
                    Al menos 8 caracteres
                  </Typography>
                </Box>

                {/* Mayúscula */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {passwordStrength.hasUppercase ? (
                    <Check sx={{ fontSize: 16, color: '#4caf50' }} />
                  ) : (
                    <Close sx={{ fontSize: 16, color: '#f44336' }} />
                  )}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: passwordStrength.hasUppercase ? '#4caf50' : '#f44336',
                      fontSize: '0.85rem'
                    }}
                  >
                    Una letra mayúscula
                  </Typography>
                </Box>

                {/* Minúscula */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {passwordStrength.hasLowercase ? (
                    <Check sx={{ fontSize: 16, color: '#4caf50' }} />
                  ) : (
                    <Close sx={{ fontSize: 16, color: '#f44336' }} />
                  )}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: passwordStrength.hasLowercase ? '#4caf50' : '#f44336',
                      fontSize: '0.85rem'
                    }}
                  >
                    Una letra minúscula
                  </Typography>
                </Box>

                {/* Número */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {passwordStrength.hasNumber ? (
                    <Check sx={{ fontSize: 16, color: '#4caf50' }} />
                  ) : (
                    <Close sx={{ fontSize: 16, color: '#f44336' }} />
                  )}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: passwordStrength.hasNumber ? '#4caf50' : '#f44336',
                      fontSize: '0.85rem'
                    }}
                  >
                    Un número
                  </Typography>
                </Box>

                {/* Coincidencia de contraseñas */}
                {passwords.confirmPassword && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {passwordStrength.passwordsMatch ? (
                      <Check sx={{ fontSize: 16, color: '#4caf50' }} />
                    ) : (
                      <Close sx={{ fontSize: 16, color: '#f44336' }} />
                    )}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: passwordStrength.passwordsMatch ? '#4caf50' : '#f44336',
                        fontSize: '0.85rem'
                      }}
                    >
                      Las contraseñas coinciden
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Barra de progreso visual */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem', color: '#666' }}>
                  Fortaleza de la contraseña:
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  {[1, 2, 3, 4].map((level) => {
                    const completedRequirements = Object.values(passwordStrength).slice(0, 4).filter(Boolean).length;
                    const isActive = completedRequirements >= level;
                    return (
                      <Box
                        key={level}
                        sx={{
                          height: 6,
                          flex: 1,
                          borderRadius: 3,
                          backgroundColor: isActive 
                            ? (completedRequirements <= 1 ? '#f44336' : 
                               completedRequirements <= 2 ? '#ff9800' : 
                               completedRequirements <= 3 ? '#2196f3' : '#4caf50')
                            : '#e0e0e0',
                          transition: 'all 0.3s ease'
                        }}
                      />
                    );
                  })}
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      ml: 1, 
                      fontSize: '0.75rem',
                      color: Object.values(passwordStrength).slice(0, 4).filter(Boolean).length >= 4 ? '#4caf50' : '#666'
                    }}
                  >
                    {
                      Object.values(passwordStrength).slice(0, 4).filter(Boolean).length >= 5 ? 'Fuerte' : 
                      Object.values(passwordStrength).slice(0, 4).filter(Boolean).length >= 4 ? 'Buena' :
                      Object.values(passwordStrength).slice(0, 4).filter(Boolean).length >= 3 ? 'Regular' : 
                      Object.values(passwordStrength).slice(0, 4).filter(Boolean).length >= 2 ? 'Débil' : 'Muy Débil'
                    }
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Información de requisitos de contraseña */}
          <Alert 
            severity="info" 
            sx={{ 
              mt: 2, 
              bgcolor: 'rgba(74, 20, 140, 0.05)',
              '& .MuiAlert-icon': {
                color: '#4a148c'
              }
            }}
          >
            La contraseña debe tener al menos 8 caracteres e incluir una mayúscula, una minúscula y un número.
          </Alert>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              backgroundColor: '#4a148c',
              '&:hover': {
                backgroundColor: '#6a1b9a',
              },
              '&:disabled': {
                backgroundColor: '#ccc',
              },
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={20} color="inherit" />
                Actualizando contraseña...
              </Box>
            ) : (
              'Actualizar Contraseña'
            )}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate('/sign-in')}
            disabled={loading}
            sx={{
              py: 1.5,
              color: '#4a148c',
              borderColor: '#4a148c',
              '&:hover': {
                borderColor: '#6a1b9a',
                backgroundColor: 'rgba(74, 20, 140, 0.04)',
              },
            }}
          >
            Volver al Inicio de Sesión
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default ChangePasswordController;
